import { DestroyRef, inject, Injectable, Injector, OnDestroy, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, interval, Observable, shareReplay, switchMap } from 'rxjs';
import { ChatFacade } from 'src/shared/stores/chat/chat-facade.store';

/**
 * Trials-only service that polls chat availability, with no dependency
 * on OnDemandStore or AppointmentsStore.
 */
@Injectable()
export class TrialsChatAvailabilityPollingService implements OnDestroy {
  private readonly chatStore = inject(ChatFacade);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private chatPollSubscription!: { destroy: () => void };

  constructor() {
    this.startPollingChatAvailability();
  }

  ngOnDestroy(): void {
    this.chatPollSubscription?.destroy();
  }

  startPollingChatAvailability(): void {
    this.chatPollSubscription?.destroy();

    runInInjectionContext(this.injector, () => {
      const chatServiceDocId$ = toObservable(this.chatStore.data.chatServiceDocId).pipe(
        filter((docId): docId is string => docId != null && docId !== ''),
        shareReplay({ bufferSize: 1, refCount: true })
      );

      const chatUpdates$ = interval(this.chatStore.availability.refreshInterval()).pipe(
        switchMap(() => chatServiceDocId$)
      ) as Observable<string>;

      chatServiceDocId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((chatServiceDocId) => {
        this.chatStore.availability.setChatAvailability(chatServiceDocId);
      });

      this.chatPollSubscription = this.chatStore.availability.setChatAvailability(chatUpdates$) as {
        destroy: () => void;
      };
    });
  }
}
